import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'

/**
 * Mounts a fixed, click-through overlay and plays the cursor-effect sprite burst at the pointer
 * on every click, app-wide. The Pixi-backed controller is dynamically imported so Pixi stays out
 * of the entry bundle — the effect simply starts working once that chunk has loaded.
 *
 * Gated on the Ultra-mode graphics setting: when ultra mode is off the decorative click sparkle is
 * fully disabled (no Pixi canvas mounted). Toggling the setting starts/destroys it on the fly.
 */

/** Above every in-app layer (modals top out ~20000) — decorative, so it sits on top of all UI. */
const OVERLAY_Z_INDEX = 2147483000

export default function CursorClickEffect() {
  const containerRef = useRef<HTMLDivElement>(null)
  const ultraModeEnabled = useSelector((s: RootState) => s.app.settings.ultraModeEnabled)

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof window === 'undefined') return
    if (!ultraModeEnabled) return

    let cancelled = false
    let handle: { destroy: () => void } | null = null

    import('./cursorClickEffectController')
      .then(async ({ startCursorClickEffect }) => {
        if (cancelled) return
        handle = await startCursorClickEffect(container)
        if (cancelled) {
          handle.destroy()
          handle = null
        }
      })
      .catch((err) => console.warn('[CursorClickEffect] init failed', err))

    return () => {
      cancelled = true
      handle?.destroy()
      handle = null
    }
  }, [ultraModeEnabled])

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: OVERLAY_Z_INDEX }}
    />
  )
}
